import { A } from '@adornis/base/env-info.js';
import { subscribeByID } from '@adornis/baseql/operations/mongo.js';
import { BuchungsheldLitElement } from '@adornis/buchungsheld-components/BuchungsheldLitElement.js';
import { User } from '@adornis/buchungsheld-db/entities/user.js';
import { Workshop } from '@adornis/buchungsheld-db/entities/workshop.js';
import { WorkshopNote } from '@adornis/buchungsheld-db/entities/WorkshopNote.js';
import { Permission } from '@adornis/buchungsheld-db/enums/roles.js';
import { dataviewEmptyState } from '@adornis/buchungsheld-db/global-helpers.js';
import '@adornis/buchungsheld-theme/components/tr-button-small.js';
import '@adornis/buchungsheld-theme/components/tr-checkbox.js';
import '@adornis/buchungsheld-theme/components/tr-chip.js';
import '@adornis/buchungsheld-theme/components/tr-icon.js';
import '@adornis/buchungsheld-theme/components/tr-loader.js';
import { LocalStorageBehaviorController } from '@adornis/chemistry/controllers/local-storage-behavior-controller.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import '@adornis/chemistry/elements/components/x-flex.js';
import '@adornis/chemistry/elements/components/x-grid.js';
import '@adornis/chemistry/elements/components/x-hr.js';
import '@adornis/chemistry/elements/components/x-tabs.js';
import '@adornis/cms/client/generated-form.js';
import type { DataViewOptions } from '@adornis/data-view/client/data-view-options.js';
import type { ColumnDefinition } from '@adornis/data-view/ColumnDefinition.js';
import { View } from '@adornis/data-view/db/view.js';
import { XDialog } from '@adornis/dialog/x-dialog.js';
import { XNativeDialog } from '@adornis/dialog/x-native-dialog.js';
import { downloadLink } from '@adornis/file-utils/client/helper-methods.js';
import { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import { currentUser } from '@adornis/users/client/currentUser.js';
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { DateTime } from 'luxon';
import { filter, firstValueFrom, switchMap, timer } from 'rxjs';
import '../../../../packages/buchungsheld-components/bh-breadcrumb.js';
import '../components/bh-footer.js';
import '../components/bh-share.js';
import '../components/bh-topbar.js';
import { BHPromptWorkshopNoteModify } from '../prompts/bh-prompt-workshop-note-modify.js';
import { BHPromptWorkshopQR } from '../prompts/bh-prompt-workshop-qr.js';
import './admin/einrichtung/workshops/bh-page--admin-einrichtung-workshops-modify.js';
import './admin/einrichtung/workshops/bh-page-admin-einrichtung-workshops-edit.js';
import './profile/bh-page-profile-contracts-lecturer.js';
import './profile/bh-page-profile-events-lecturer.js';
import './profile/bh-page-profile-events-participation.js';
import './profile/bh-page-profile-info.js';

enum TabPages {
  PROFILE = 'PROFILE',
  EVENTS_PARTICIPATION = 'EVENTS_PARTICIPATION',
  EVENTS_TEACHING = 'EVENTS_TEACHING',
  CONTRACTS = 'CONTRACTS',
}
/**
 * @element bh-page-profile
 */
@customElement('bh-page-profile')
export class BHProfile extends BuchungsheldLitElement {
  @state() private readonly _user = new RXController(this, currentUser<User>());

  @state() private readonly _selectedTab = new LocalStorageBehaviorController(
    this,
    'events-participation-tab',
    TabPages.EVENTS_PARTICIPATION,
  );

  @property({ type: Object }) selectedWorkshop;
  // @property({ type: String }) selectedWorkshopID = '';
  @property({ attribute: 'selected-workshop-id' }) selectedWorkshopID = new RXController<string | null>(this, null);
  @property({ attribute: 'selected-workshop-mode' }) selectedWorkshopMode = new RXController<'modify' | 'notes' | null>(
    this,
    null,
  );

  @state() private readonly _workshop = new RXController(
    this,
    this.selectedWorkshopID.observable.pipe(
      filter(Boolean),
      switchMap(id => subscribeByID(Workshop, id)({ name: 1, _class: 1, _id: 1 })),
    ),
  );

  override render() {
    return html`
      <x-flex space="lg" ${css({ height: '100%', overflow: 'auto' })}>
        <x-section ${css({ padding: `${this.spacing.md} 0`, flexGrow: '1' })} space="lg">
          <x-tabs .selected=${this._selectedTab.value} ${css({ borderBottom: `1px solid ${this.colors.neutral}` })}>
            <x-tab
              label=${TabPages.EVENTS_PARTICIPATION}
              @click=${() => this._selectedTab.next(TabPages.EVENTS_PARTICIPATION)}
            >
              ${this._translation.translate('PROFILE', 'PARTICIPATED_EVENTS')}
            </x-tab>
            ${this._user.value?.hasPermission(Permission.VIEW_LECTURER_WORKSHOPS)
              ? html`<x-tab
                  label=${TabPages.EVENTS_TEACHING}
                  @click=${() => this._selectedTab.next(TabPages.EVENTS_TEACHING)}
                >
                  ${this._translation.translate('PROFILE', 'EVENTS_TEACHING')}
                </x-tab>`
              : nothing}
            ${this._user.value?.hasPermission(Permission.VIEW_LECTURER_CONTRACTS)
              ? html`<x-tab label=${TabPages.CONTRACTS} @click=${() => this._selectedTab.next(TabPages.CONTRACTS)}>
                  ${this._translation.translate('PROFILE', 'CONTRACTS')}
                </x-tab>`
              : nothing}
            <x-tab label=${TabPages.PROFILE} @click=${() => this._selectedTab.next(TabPages.PROFILE)}>
              ${this._translation.translate('PROFILE', 'TITLE')}
            </x-tab>
          </x-tabs>
          <x-flex flex padding="sm"> ${this._tabContent()} </x-flex>
        </x-section>
      </x-flex>
    `;
  }

  private _tabContent() {
    switch (this._selectedTab.value) {
      case TabPages.PROFILE:
        return html` <bh-page-profile-info> </bh-page-profile-info> `;
      case TabPages.EVENTS_PARTICIPATION:
        return html` <bh-page-profile-events-participation></bh-page-profile-events-participation>`;
      case TabPages.EVENTS_TEACHING:
        if (this.selectedWorkshop && this.selectedWorkshopMode.value === 'modify') {
          return this._renderWorkshopEdit();
        } else if (this.selectedWorkshop && this.selectedWorkshopMode.value === 'notes') {
          return this._renderWorkshopNotes();
        } else {
          return html`<bh-page-profile-events-lecturer
            @workshop-selected=${this.handleWorkshopSelected}
            @workshop-notes-clicked=${this.handleWorkshopNotesClicked}
          ></bh-page-profile-events-lecturer>`;
        }
      case TabPages.CONTRACTS:
        return html`<bh-page-profile-contracts-lecturer
          lecturer-id=${this._user.value?._id}
        ></bh-page-profile-contracts-lecturer>`;
      default:
        return html` ${this._translation.translate('err', 'something_went_wrong')} `;
    }
  }

  handleWorkshopSelected(event) {
    this.selectedWorkshop = event.detail;
    this.selectedWorkshopID = event.detail.workshopID;
    this.selectedWorkshopMode.value = 'modify';
  }

  handleWorkshopNotesClicked(event) {
    this.selectedWorkshop = event.detail;
    this.selectedWorkshopID = event.detail.workshopID;
    this.selectedWorkshopMode.value = 'notes';
  }

  _renderWorkshopEdit() {
    return html`
      <x-flex space="md">
        <x-flex horizontal> <!-- make only elements clickable, not the container -->
          <x-flex horizontal space="xs"
          ${css({ cursor: 'pointer' })}
          @click=${() => (this.selectedWorkshop = undefined)}>
            <x-button mode="icon" icon="arrow_upward"></x-button>
            ${this._translation.translate('PROFILE', 'OVERVIEW_TEACHINGS', 'back_to_overview')}
          </x-flex>
        </x-flex>

        <x-hr></x-hr>

        <x-flex horizontal> <!-- make only elements clickable, not the container -->
          <x-flex
            horizontal
            crossaxis-center
            space="sm"
            padding="xxs"
            ${css({ color: this.colors.accent, cursor: 'pointer' })}
            @click=${() => BHPromptWorkshopQR.showPopup({ props: { workshopID: this.selectedWorkshopID.value } })}
          >
            <x-icon> qr_code_2 </x-icon>
            <x-text> ${this._translation.translate('workshop', 'generate_participation_qrcode')}... </x-text>
          </x-flex>
        </x-flex>

        <x-hr> </x-hr>

        <x-h3> Import / Export </x-h3>
        <x-flex horizontal space="md">
          <x-button
            mode="outline"
            icon="download"
            @click=${async () => {
              console.log('workshopID', this.selectedWorkshopID.value);
              try {
                await XNativeDialog.waitFor(
                  Promise.all([
                    Workshop.exportWorkshopParticipantsCSV(this.selectedWorkshopID.value)().then(fileName =>
                      downloadLink(A.absoluteUrl(`/__export/download/${fileName}`), 'teilnehmer.csv'),
                    ),
                    firstValueFrom(timer(500)),
                  ]),
                );
              } catch (e) {
                await XNativeDialog.alert(this._translation.translate('workshop', 'export_err'));
              }
            }}
          >
            ${this._translation.translate('imp_exp', 'exp_participants')}
          </x-button>
          </x-flex>

          <x-hr></x-hr>
          
          <bh-participation-dataview .product=${this._workshop.value} .mode=${'lecturer'}></bh-participation-dataview>
        </x-flex>
      </x-flex>
    `;
  }

  _renderWorkshopNotes() {
    const dataViewOptions = {
      class: WorkshopNote,
      emptyState: () => dataviewEmptyState({}),
      selectionSet: { title: 1, createdAt: 1, text: 1 },
      view: new View({
        filter: new AdornisFilter({ searchFields: ['workshopID'], search: this.selectedWorkshopID.value }),
      }),
      customColumns: [
        { fieldName: 'title' },
        {
          fieldName: 'createdAt',
          columnWidth: 'max-content',
          getFieldMapping: instance => instance.createdAt.toLocaleString(DateTime.DATE_MED),
        },
        {
          columnName: '',
          customID: '_options',
          selectionSet: { adminOnly: 1 },
          renderItem: instance => {
            return html`
              <x-flex space="xs" wrap horizontal>
                <tr-button-small
                  icon="edit"
                  .color=${this.colors.secondary.shade(300)}
                  .action=${() => BHPromptWorkshopNoteModify.edit(instance)}
                ></tr-button-small>
                <tr-button-small
                  icon="delete"
                  .color=${this.colors.tone.error}
                  .action=${async () => {
                    if (!(await XDialog.confirm(this._translation.translate('settings', 'del_crt_cnf')))) return;
                    await instance.remove();
                  }}
                ></tr-button-small>
              </x-flex>
            `;
          },
          columnWidth: 'max-content',
        },
      ] as Array<ColumnDefinition<typeof WorkshopNote>>,
    } satisfies DataViewOptions<typeof WorkshopNote>;

    return html`
      <x-flex space="md">
        <x-flex horizontal>
          <!-- make only elements clickable, not the container -->
          <x-flex
            horizontal
            space="xs"
            ${css({ cursor: 'pointer' })}
            @click=${() => (this.selectedWorkshop = undefined)}
          >
            <x-button mode="icon" icon="arrow_upward"></x-button>
            ${this._translation.translate('PROFILE', 'OVERVIEW_TEACHINGS', 'back_to_overview')}
          </x-flex>
        </x-flex>

        <x-hr></x-hr>

        <x-flex horizontal>
          <!-- make only elements clickable, not the container -->
          <x-flex
            horizontal
            crossaxis-center
            space="sm"
            padding="xxs"
            ${css({ color: this.colors.accent, cursor: 'pointer' })}
            @click=${() =>
              BHPromptWorkshopNoteModify.add(this.selectedWorkshopID.value!).then(_ => this.requestUpdate())}
          >
            <x-icon> description </x-icon>
            <x-text> ${this._translation.translate('PROMPT', 'add_workshop_note')}... </x-text>
          </x-flex>
        </x-flex>

        <x-hr></x-hr>

        <x-h3> Notizen </x-h3>
        <bh-workshop-notes .workshopID=${this.selectedWorkshopID.value}></bh-workshop-notes>
        <x-data-view .options=${dataViewOptions}></x-data-view>
      </x-flex>
    `;
  }
}
